import { defaultPortalPath } from '../../navigation/portalNavigation';

const AUTHORIZATION_URL = 'https://accounts.feishu.cn/open-apis/authen/v1/authorize';
const LOCAL_DEV_ORIGIN = 'http://127.0.0.1:5175';
const LOCAL_DEV_START_PARAM = 'feishuWebStart';
const PENDING_KEY = 'platform-feishu-web-login';
const PENDING_MAX_AGE_MS = 10 * 60 * 1000;

type PendingLogin = { appId: string; state: string; createdAt: number };

export function hasFeishuWebCallback(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.has('code') || params.has('state') || params.has('error');
}

export function hasFeishuWebStartHandoff(search: string): boolean {
  return import.meta.env.DEV && new URLSearchParams(search).get(LOCAL_DEV_START_PARAM) === '1';
}

export function startFeishuWebLogin(appId: string): void {
  if (!appId || appId.length > 128) throw new Error('飞书应用配置无效');
  // Localhost and 127.0.0.1 have separate sessionStorage. Move first, then create state.
  if (import.meta.env.DEV && window.location.protocol === 'http:'
      && window.location.hostname === 'localhost' && window.location.port === '5175') {
    const handoff = new URL('/login', LOCAL_DEV_ORIGIN);
    handoff.searchParams.set('feishuAppId', appId);
    handoff.searchParams.set(LOCAL_DEV_START_PARAM, '1');
    window.location.assign(handoff.toString());
    return;
  }
  const stateBytes = crypto.getRandomValues(new Uint8Array(32));
  const state = Array.from(stateBytes, byte => byte.toString(16).padStart(2, '0')).join('');
  const pending: PendingLogin = { appId, state, createdAt: Date.now() };
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));

  const url = new URL(AUTHORIZATION_URL);
  url.searchParams.set('client_id', appId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('redirect_uri', `${window.location.origin}/login`);
  url.searchParams.set('scope', 'auth:user.id:read');
  url.searchParams.set('state', state);
  window.location.assign(url.toString());
}

export function consumeFeishuWebCallback(search: string): {
  appId: string;
  code?: string;
  error?: string;
} {
  const raw = sessionStorage.getItem(PENDING_KEY);
  sessionStorage.removeItem(PENDING_KEY);
  let pending: PendingLogin;
  try {
    pending = JSON.parse(raw ?? '') as PendingLogin;
  } catch {
    throw new Error('飞书登录请求已失效，请重新发起');
  }
  const params = new URLSearchParams(search);
  if (!pending || typeof pending.appId !== 'string' || pending.appId.length < 1
      || pending.appId.length > 128 || typeof pending.state !== 'string'
      || typeof pending.createdAt !== 'number' || !Number.isFinite(pending.createdAt)
      || Date.now() - pending.createdAt > PENDING_MAX_AGE_MS
      || Date.now() < pending.createdAt
      || params.getAll('state').length !== 1
      || params.get('state') !== pending.state) {
    throw new Error('飞书登录状态校验失败，请重新发起');
  }
  if (params.has('error')) return { appId: pending.appId, error: '飞书授权未完成' };
  const codes = params.getAll('code');
  if (codes.length !== 1 || !codes[0] || codes[0].length > 4096) {
    throw new Error('飞书授权码无效，请重新发起');
  }
  return { appId: pending.appId, code: codes[0] };
}

export function feishuWebLoginReturnPath(appId: string): string {
  return `${defaultPortalPath}?feishuAppId=${encodeURIComponent(appId)}`;
}
